import React from 'react';
import HeroBanner from '@ajat/design-system/ContentBlocks/HeroBanner';
import { ComponentPage, DemoCard } from '../../shared';
import { HERO_IMAGE } from './contentDemoData';

export const meta = {
  component: HeroBanner,
  title: 'Hero Banner',
  group: 'Content Blocks',
  importPath: '@ajat/design-system/ContentBlocks/HeroBanner',
  description:
    'The top block of a landing page — subtitle, headline, copy, CTA and a visual. Every text slot has a matching show* flag, so a hero can be reduced to a headline and a button without conditionals in the page.',
  props: [
    ['showSubtitle', 'bool', 'true', 'Show the subtitle'],
    ['showTitle', 'bool', 'true', 'Show the headline'],
    ['showCopy', 'bool', 'true', 'Show the body copy'],
    ['showCta', 'bool', 'true', 'Show the CTA button'],
    ['showCaption', 'bool', 'true', 'Show the caption under the CTA'],
    ['subtitle', 'string', "''", 'Eyebrow above the headline (typo-h5)'],
    ['title', 'string', "''", 'Headline'],
    ['copy', 'string', "''", 'Body copy'],
    ['caption', 'string', "''", 'Small note under the CTA'],
    ['ctaLabel', 'string', "''", 'CTA label'],
    ['ctaVariant', 'string', "'alternative'", 'Button variant'],
    ['ctaIcon', 'node', '<ArrowRight />', 'CTA icon'],
    ['ctaOnClick', 'func', '—', 'CTA handler'],
    ['ctaHref', 'string', '—', 'CTA link target'],
    ['image', 'string', 'null', 'Visual beside the content'],
    ['imageAlt', 'string', "''", 'Alt text for the visual'],
    ['overlay', 'node', 'null', 'Absolutely positioned overlay, e.g. the open-shop card'],
    ['contentSlot', 'node', 'null', 'Extra content injected under the copy'],
    ['backgroundColor', 'string', 'null', 'Section background colour'],
    ['backgroundImage', 'string', 'null', 'Section background image'],
    ['theme', "'light' | 'dark'", "'light'", 'Dark inverts the text colours'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  usage: [
    {
      title: 'On a landing page',
      code: `import HeroBanner from '@ajat/design-system/ContentBlocks/HeroBanner';

<HeroBanner
  subtitle="BEST PRICE GUARANTEED"
  title="Kläder för ökad samhörighet"
  copy="Med våra 20-års erfarenhet …"
  ctaLabel="Köp Skolmerch"
  ctaVariant="alternative"
  ctaOnClick={() => onMenuItemClick('menu_1', { toSchoolShop: true })}
  caption="Gratis trøjepakke til to repræsentanter"
  image={heroImage}
  imageAlt="Studietur collection"
/>`,
    },
  ],
};

export default function HeroBannerPage() {
  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Light theme" hint="default — gray-1 ground" flush>
        <HeroBanner
          subtitle="BEST PRICE GUARANTEED"
          title="Kläder för ökad samhörighet"
          copy="Med våra 20-års erfarenhet kan vi se till att skapa merch i hög kvalité oavsett om du är en skola, elevkår eller förening!"
          ctaLabel="Köp Skolmerch"
          ctaVariant="alternative"
          caption="Gratis trøjepakke til to repræsentanter"
          image={HERO_IMAGE}
          imageAlt="Studietur collection"
        />
      </DemoCard>

      <DemoCard title="Dark theme with a background image" flush>
        <HeroBanner
          theme="dark"
          backgroundImage={HERO_IMAGE}
          subtitle="BEST PRICE GUARANTEED"
          title="Kläder för ökad samhörighet"
          copy="Med våra 20-års erfarenhet kan vi se till att skapa merch i hög kvalité oavsett om du är en skola, elevkår eller förening!"
          ctaLabel="Köp Skolmerch"
          ctaVariant="alternative"
          showCaption={false}
        />
      </DemoCard>

      <DemoCard title="Dark theme with a solid ground" flush>
        <HeroBanner
          theme="dark"
          subtitle="STUDENT CLUB"
          title="Design tøj til jeres studietur"
          copy="Studieturen er en rejse, som skaber glæde og mindeværdige oplevelser."
          ctaLabel="Bestil nu"
          ctaVariant="main"
          image={HERO_IMAGE}
          imageAlt="Studietur tøj"
          caption="Gratis trøjepakke til to repræsentanter"
        />
      </DemoCard>

      <DemoCard title="Minimal" hint="no image, no caption" flush>
        <HeroBanner
          subtitle="OUR MISSION"
          title="High-quality clothing for schools"
          copy="We create premium custom apparel for schools, student clubs, and organizations across Scandinavia."
          ctaLabel="Learn More"
          ctaVariant="outline"
          showCaption={false}
        />
      </DemoCard>

      <DemoCard title="Custom background colour" flush>
        <HeroBanner
          backgroundColor="var(--color-gray-025)"
          subtitle="FEATURED COLLECTION"
          title="Discover our latest merch"
          copy="Premium quality, custom designs, fast delivery."
          ctaLabel="Shop Now"
          ctaVariant="black"
          image={HERO_IMAGE}
          imageAlt="Merch collection"
          showCaption={false}
        />
      </DemoCard>
    </ComponentPage>
  );
}
